import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, X } from 'lucide-react';
import { useTeam } from 'shared/context/teamContext';
import { enterDemoTeam } from 'shared/utils/demoTeam';
import { fetchTeamActivation, markTeamActivatedLocal } from 'shared/utils/onboardingActivation';
import usePolling from 'shared/hooks/usePolling';
import MembersPage from 'features/settings/components/panels/MembersPage';

/**
 * The install flow, rendered INSIDE the app shell.
 *
 * The standalone install wall (InstallProjectPage / InstallDeployPage) replaced
 * the whole screen, sidebar included. That meant a team which had not
 * instrumented yet could not reach Settings at all — no inviting a teammate to
 * do the wiring, and no upgrading to Pro until after tracing was live. Billing
 * was gated behind the very step a customer might be stuck on.
 *
 * So this is the page CONTENT for a team with no traces, while the shell keeps
 * rendering around it. Both steps live here rather than on separate routes,
 * because navigating to a second full-page route is what took the sidebar away
 * in the first place.
 *
 * Activation is authoritative server-side (teams.activated_at); step 2 just
 * watches for the flip and then reveals the real page.
 */

const ACCENT = 'var(--trodo-primary)';
const MANUAL_DOCS_URL = 'https://docs.trodo.ai/observability/features/instrumentation/guide';
const POLL_MS = 3000;
const MAX_POLL_MS = 10000;

const InstallPanel = ({ onActivated }) => {
  const navigate = useNavigate();
  const { selectedTeam, setSelectedTeam } = useTeam();

  const [step, setStep] = useState('prompt'); // 'prompt' | 'waiting'
  const [copied, setCopied] = useState(false);
  const [enteringDemo, setEnteringDemo] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const doneRef = useRef(false);

  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;

  // Real site ID from the team payload; the placeholder keeps the prompt
  // readable while the team is still loading.
  const siteId = selectedTeam?.siteId || '<your-site-id>';

  const prompt = useMemo(
    () =>
      `Install the Trodo agent-tracing skill from github.com/trodoai/skills\n`
      + `(npx skills add trodoai/skills), then follow it to add tracing to every agent\n`
      + `in this project. My Trodo site ID is ${siteId}. Map all agent entry points and\n`
      + `show me the run plan before writing code.`,
    [siteId],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — the text is still selectable */
    }
  };

  const handleTryDemo = async () => {
    setDemoError('');
    setEnteringDemo(true);
    try {
      await enterDemoTeam(navigate, setSelectedTeam);
    } catch (err) {
      setDemoError(err?.response?.data?.message || err?.message || 'Could not open the demo.');
      setEnteringDemo(false);
    }
  };

  // Step 2: poll until the first run lands, then hand the page back. 3s,
  // backing off to 10s while they deploy, and at once when they come back to
  // this tab from their terminal. Nothing while the tab is hidden.
  const waiting = step === 'waiting' && teamId != null;
  usePolling(async (isActive) => {
    const { activated } = await fetchTeamActivation(teamId);
    if (!isActive() || doneRef.current) return false;
    if (!activated) return true;
    doneRef.current = true;
    markTeamActivatedLocal(teamId);
    // Flip the cached team so the shell stops gating and the real page shows.
    if (setSelectedTeam) setSelectedTeam({ ...selectedTeam, activated: true });
    if (onActivated) onActivated();
    return false;
  }, { enabled: waiting, interval: POLL_MS, maxInterval: MAX_POLL_MS, key: teamId });

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [waiting]);

  const mins = Math.floor(elapsed / 60);
  const elapsedLabel = mins > 0 ? `${mins}m ${elapsed % 60}s` : `${elapsed}s`;

  return (
    <div className="mx-auto w-full max-w-2xl px-1 py-8">
      {step === 'prompt' ? (
        <>
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <h1 className="text-[1.75rem] font-normal leading-tight tracking-tight text-[color:var(--text-primary)]">
              Connect your agents
            </h1>
            {/* Whoever opens this screen is often not the person who will write
                the instrumentation. Handing them the invite right here beats
                making them find Settings → Members first. */}
            <button
              type="button"
              onClick={() => setShowMembers(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-2.5 py-1.5 text-[13px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)]"
            >
              <UserPlus size={16} strokeWidth={1.75} />
              Invite developer
            </button>
          </div>
          <p className="mb-8 text-[15px] leading-relaxed text-[color:var(--text-muted)]">
            Paste this into your coding agent and it will install Trodo tracing end to end. Nothing
            appears on your dashboards until the first run arrives.
          </p>

          <div className="overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-surface">
            <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-4 py-2.5">
              <span className="text-xs font-medium uppercase tracking-wide text-[color:var(--text-muted)]">
                coding agent
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-md px-2.5 py-1 text-xs font-medium text-ink-onBrand transition-colors hover:opacity-[0.96]"
                style={{ backgroundColor: ACCENT }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre className="whitespace-pre-wrap px-4 py-4 font-mono text-[14px] leading-relaxed text-[color:var(--text-primary)]">
{prompt}
            </pre>
          </div>

          <p className="mt-4 text-[13px] text-[color:var(--text-muted)]">
            Prefer to wire it up by hand?{' '}
            <a
              href={MANUAL_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline transition-colors hover:text-[color:var(--text-primary)]"
            >
              Manual instrumentation guide
            </a>
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleTryDemo}
              disabled={enteringDemo}
              className={`flex-1 rounded-lg border border-[color:var(--border-default)] px-5 py-2.5 text-[15px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] ${
                enteringDemo ? 'cursor-not-allowed opacity-60' : ''
              }`}
            >
              {enteringDemo ? 'Opening demo…' : 'Check out an example project'}
            </button>
            <button
              type="button"
              onClick={() => setStep('waiting')}
              className="flex-1 rounded-lg px-5 py-2.5 text-[15px] font-medium text-ink-onBrand transition-opacity hover:opacity-[0.96]"
              style={{ backgroundColor: ACCENT }}
            >
              The agent is done →
            </button>
          </div>

          {demoError && (
            <div className="mt-3 rounded-lg border border-danger bg-danger-soft p-3 text-sm text-danger">
              {demoError}
            </div>
          )}
        </>
      ) : (
        <>
          <h1 className="mb-3 text-[1.75rem] font-normal leading-tight tracking-tight text-[color:var(--text-primary)]">
            Deploy the code
          </h1>
          <p className="mb-8 text-[15px] leading-relaxed text-[color:var(--text-muted)]">
            Push it to production or a sandbox as you normally would, or just run it locally. We'll
            tell you the moment events start arriving.
          </p>

          <div className="flex items-center gap-4 rounded-xl border border-[color:var(--border-default)] bg-surface px-5 py-6">
            <span
              className="inline-block h-3 w-3 shrink-0 animate-pulse rounded-full"
              style={{ backgroundColor: ACCENT }}
            />
            <div className="flex-1">
              <div className="text-[15px] font-medium text-[color:var(--text-primary)]">
                Waiting for your first event…
              </div>
              <div className="mt-0.5 text-[13px] text-[color:var(--text-muted)]">
                Listening for {elapsedLabel}. This page moves on by itself.
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setStep('prompt')}
            className="mt-9 text-sm text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
          >
            ← Back to install
          </button>
        </>
      )}

      {/* Invite popup: the real Settings → Members panel, so there is one
          implementation of inviting.

          The z-index ladder matters and is easy to break: this overlay sits at
          1200, BELOW MUI's Dialog default (1300) so the panel's own "Add
          member" dialog opens above it, and below AppDialog's 1400 so a
          "remove member" confirm opens above them both. */}
      {showMembers && (
        <div
          className="fixed inset-0 flex items-start justify-center overflow-y-auto p-3 sm:p-6"
          style={{ backgroundColor: 'rgba(15,23,42,0.45)', zIndex: 1200 }}
          onMouseDown={() => setShowMembers(false)}
        >
          <div
            className="my-auto w-full max-w-5xl rounded-2xl border border-[color:var(--border-default)] bg-surface shadow-[0_20px_60px_-15px_rgba(15,23,42,0.35)]"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-6 py-4">
              <h2 className="text-[16px] font-semibold text-[color:var(--text-primary)]">
                Team members
              </h2>
              <button
                type="button"
                onClick={() => setShowMembers(false)}
                aria-label="Close"
                className="rounded-md p-1.5 text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)]"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="max-h-[82vh] overflow-y-auto px-6 py-5">
              <MembersPage />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstallPanel;
